import { ReactNode } from 'react';
import Link from 'next/link';
import Icon from '@components/common/assets';
import { useUserInfo } from '@repository/auth/useUserInfo';
import {
  useDchampsApplyStatus,
  useDchampsGrade,
  useDchampsInfo,
  useDhampsFinalsCheck,
} from '@repository/dchamps/useDchamps';
import { GradeType } from '@domain/constant/gradeType';
import { gradeTextSelectMap } from '@domain/constant/gradeType';
import Colors from '@domain/constant/colors';
import { DchampsEventMothText, DchampsEventType } from '@domain/constant/dchamps/eventType';
import { V2RouteUrl } from '@utils/routeUtil';
import classnames from 'classnames/bind';
import styles from './todoArea.module.scss';

const cx = classnames.bind(styles);

type iconType = 'profile' | 'emblem' | GradeType;

interface DchampsToDoStatusItemProps {
  title: string;
  label: string;
  check: boolean;
}

const DchampsToDoStatusItem = ({ title, label, check }: DchampsToDoStatusItemProps) => {
  return (
    <div className={cx('statusItem')}>
      <div className={cx('itemInfo')}>
        <span className={cx('item_title')}>{title}</span>
        <span className={cx('item_divider')}>|</span>
        <span className={cx('item_label')}>{label}</span>
      </div>
      <div className={cx('checkArea')}>
        {check ? (
          <>
            <Icon name="checkCircleLightFilled" width={32} height={32} fill={Colors.C_GREEN_150} />
            <span className={cx('text', { check })}>완료</span>
          </>
        ) : (
          <>
            <Icon name="checkCircleLightFilled" width={32} height={32} fill={Colors.C_COOL_GRAY_70} />
            <span className={cx('text')}>미완료</span>
          </>
        )}
      </div>
    </div>
  );
};

const DchampsToDoStatus = () => {
  const { data: userData } = useUserInfo();
  const isLogin = !!userData?.id;
  const { data: dchampsInfo } = useDchampsInfo();
  const quailifyEventType = dchampsInfo?.progressQualify?.eventType;
  const finalEventType = dchampsInfo?.progressFinal?.eventType;
  const { data: isFinalist } = useDhampsFinalsCheck(isLogin && !!finalEventType, finalEventType as DchampsEventType);
  const eventType = isFinalist ? finalEventType : quailifyEventType;
  const { data: applyStatusData } = useDchampsApplyStatus(isLogin && !!eventType, eventType as DchampsEventType);
  const titleText = `${DchampsEventMothText[eventType as DchampsEventType]}월 디챔스 참가 조건`;

  if (!dchampsInfo?.progressQualify && !dchampsInfo?.progressFinal?.eventType) return null;

  return (
    <div className={cx('toDoStatusArea')}>
      <div className={cx('statusInfo')}>
        <span className={cx('title')}>{titleText}</span>
        {!!applyStatusData?.isAllApplied && <span className={cx('badge')}>참가완료</span>}
      </div>
      <div className={cx('divider')} />
      <div className={cx('statusList')}>
        <DchampsToDoStatusItem title={'개발자 역검'} label={'역량검사'} check={!!applyStatusData?.isPaccApplied} />
        <DchampsToDoStatusItem
          title={'개발 구현 능력 검사'}
          label={'코딩테스트'}
          check={!!applyStatusData?.isPstApplied}
        />
      </div>
    </div>
  );
};

interface DChampsToDoItemProps {
  mainText: ReactNode | string;
  href?: string;
  iconType?: iconType;
}

const DchampsToDoItem = ({ mainText, href, iconType }: DChampsToDoItemProps) => {
  return (
    <div className={cx('toDoItem')}>
      <div>
        {href ? (
          <Link href={href} legacyBehavior>
            <a
              className={cx('mainText', { clickable: !!href })}
              role="button"
              target={iconType === 'emblem' ? '_blank' : ''}
            >
              {mainText}
            </a>
          </Link>
        ) : (
          <a className={cx('mainText', { clickable: !!href })}>{mainText}</a>
        )}
      </div>
      <div className={cx('icon', iconType)} />
    </div>
  );
};

const DchampsToDoList = () => {
  const { data: userData } = useUserInfo();
  const isLogin = !!userData?.id;
  const { data: dchampsInfo } = useDchampsInfo();
  const eventType = dchampsInfo?.devChampsInfoDtos[0].eventType;
  const { data: dChampsGradeData } = useDchampsGrade(isLogin && !!eventType, eventType as DchampsEventType);
  const grade = dChampsGradeData?.grade; // 최고등급

  return (
    <div className={cx('toDoListArea')}>
      {grade ? (
        <DchampsToDoItem
          mainText={
            // eslint-disable-next-line react/jsx-wrap-multilines
            <>
              <span className={cx('subText')}>{`${userData?.name}님의 등급은`}</span>
              <span className={cx('emphasis', grade)}>{gradeTextSelectMap[grade]}</span>
              입니다.
            </>
          }
          iconType={grade}
        />
      ) : (
        <DchampsToDoItem
          mainText={'나의 디챔스 등급 확인하기'}
          iconType={'emblem'}
          href={V2RouteUrl.PHS_PST_TEST_GATE}
        />
      )}
      <DchampsToDoItem mainText={'내 프로필 입력하기'} iconType={'profile'} href={V2RouteUrl.PROFILE} />
    </div>
  );
};

const DChampsToDoArea = () => {
  return (
    <div className={cx('toDoArea')}>
      <DchampsToDoStatus />
      <DchampsToDoList />
    </div>
  );
};

export default DChampsToDoArea;
